import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { ScanLine, Loader2 } from "lucide-react";

function CameraView({ onCode }) {
  const ref = useRef(null);
  const [err, setErr] = useState("");
  const cb = useRef(onCode);
  cb.current = onCode;
  useEffect(() => {
    const scanner = new Html5Qrcode("case-qr-reader");
    ref.current = scanner;
    let done = false;
    scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 }, (text) => {
      if (done) return;
      done = true;
      cb.current(text);
    }).catch(() => setErr("Camera not available — allow camera access or type the case number below."));
    return () => { if (scanner.isScanning) scanner.stop().catch(() => {}); };
  }, []);
  return (
    <div>
      <div id="case-qr-reader" data-testid="scan-camera" className="w-full rounded-lg overflow-hidden bg-slate-100 min-h-[200px]" />
      {err && <p className="text-xs text-amber-700 mt-2" data-testid="scan-camera-error">{err}</p>}
    </div>
  );
}

export function ScanCaseButton() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const find = async (value) => {
    if (!value.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get("/cases/lookup", { params: { code: value } });
      setOpen(false);
      navigate(`/app/cases/${data.id}/results`);
    } catch {
      toast.error("No case found for this code in your account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="scan-case-btn"><ScanLine className="w-4 h-4 mr-1.5" /> Scan QR</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="scan-case-dialog">
          <DialogHeader><DialogTitle className="font-heading">Open case from lab request</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Point the camera at the QR code on the patient's lab request.</p>
          {open && <CameraView onCode={find} />}
          <form onSubmit={(e) => { e.preventDefault(); find(code); }} className="flex gap-2 mt-2">
            <Input data-testid="scan-manual-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Or type case no. (DC-123456) or file no." />
            <Button type="submit" disabled={busy} data-testid="scan-manual-submit" className="bg-sky-700 hover:bg-sky-800">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Open"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
